import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Error boundary component for bulk analysis functionality
 * Provides graceful error handling and recovery mechanisms
 */
class BulkAnalysisErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('BulkAnalysisErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // TODO: Send error to logging service
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReset = () => {
    // Clear all state and reload the page
    window.location.reload();
  };

  handleExportErrorReport = () => {
    const errorReport = {
      timestamp: new Date().toISOString(),
      error: this.state.error?.toString(),
      errorInfo: this.state.errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount
    };

    const dataStr = JSON.stringify(errorReport, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `bulk-analysis-error-${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="bulk-analysis-error-boundary p-6">
          <Card className="max-w-2xl mx-auto p-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Something went wrong
              </h1>
              <p className="text-gray-600">
                The bulk analysis page encountered an unexpected error. 
                Don't worry, your data is safe.
              </p>
            </div>

            <Alert className="mb-6">
              <AlertDescription>
                <strong>Error:</strong> {this.state.error?.message || 'Unknown error occurred'}
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button 
                  onClick={this.handleRetry}
                  variant="default"
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={this.handleReset}
                  variant="outline"
                  className="w-full"
                >
                  Reset Page
                </Button>
                <Button 
                  onClick={this.handleExportErrorReport}
                  variant="outline"
                  className="w-full"
                >
                  Export Error Report
                </Button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">
                  If the problem persists, please contact support with the error report.
                </p>
                <div className="text-xs text-gray-400">
                  Retry attempts: {this.state.retryCount}
                </div>
              </div>
            </div>

            {/* Development Error Details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 p-4 bg-gray-50 rounded border">
                <summary className="cursor-pointer font-medium text-sm mb-2">
                  Development Error Details
                </summary>
                <div className="text-xs font-mono space-y-2">
                  <div>
                    <strong>Error:</strong>
                    <pre className="mt-1 p-2 bg-red-50 border border-red-200 rounded overflow-auto">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 p-2 bg-gray-100 border rounded overflow-auto">
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                  <div>
                    <strong>Error Stack:</strong>
                    <pre className="mt-1 p-2 bg-gray-100 border rounded overflow-auto">
                      {this.state.error.stack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default BulkAnalysisErrorBoundary;